import { useCallback, useEffect, useRef, useState } from "react";

const MAX_FREE_BYTES = 10 * 1024 * 1024;
const MAX_PAID_BYTES = 50 * 1024 * 1024;

interface DropZoneProps {
  onFile: (file: File) => void;
  paid: boolean;
  busy: boolean;
}

export function DropZone({ onFile, paid, busy }: DropZoneProps) {
  const [active, setActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const max = paid ? MAX_PAID_BYTES : MAX_FREE_BYTES;

  const accept = useCallback(
    (file: File) => {
      setError(null);
      if (!/^image\/(png|jpe?g)$/.test(file.type)) {
        setError("PNG or JPG only.");
        return;
      }
      if (file.size > max) {
        setError(
          paid
            ? `File too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max 50 MB.`
            : `File too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Free tier max 10 MB; upgrade for 50 MB.`
        );
        return;
      }
      onFile(file);
    },
    [max, onFile, paid]
  );

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setActive(false);
      const file = e.dataTransfer.files[0];
      if (file) accept(file);
    },
    [accept]
  );

  // Paste from clipboard anywhere on the page.
  useEffect(() => {
    const handler = (e: ClipboardEvent) => {
      const item = Array.from(e.clipboardData?.items ?? []).find((i) =>
        i.type.startsWith("image/")
      );
      const file = item?.getAsFile();
      if (file) accept(file);
    };
    window.addEventListener("paste", handler);
    return () => window.removeEventListener("paste", handler);
  }, [accept]);

  return (
    <div className="space-y-3">
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setActive(true);
        }}
        onDragLeave={() => setActive(false)}
        onDrop={onDrop}
        onClick={() => fileInput.current?.click()}
        className={`border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition ${
          active ? "dropzone-active" : "border-ink/15"
        } ${busy ? "opacity-60 pointer-events-none" : ""}`}
        role="button"
        aria-label="Upload chart screenshot"
      >
        <input
          ref={fileInput}
          type="file"
          accept="image/png,image/jpeg"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) accept(f);
            e.target.value = "";
          }}
        />
        <div className="text-lg font-medium mb-1">
          Drop a chart screenshot, paste, or click to upload
        </div>
        <div className="text-sm text-ink/60">
          PNG or JPG · up to {paid ? "50 MB" : "10 MB"} ·{" "}
          {paid ? "unlimited" : "5 free / day"}
        </div>
      </div>
      {error && <div className="text-sm text-red-600">{error}</div>}
    </div>
  );
}
